<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  // null => create mode; an object => edit mode.
  audienceList: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const uiFlags = useMapGetter('audienceLists/getUIFlags');

const state = reactive({ name: '', description: '' });

const rules = {
  name: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(rules, state);

const isEditing = computed(() => Boolean(props.audienceList?.id));

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const syncFromProps = () => {
  state.name = props.audienceList?.name ?? '';
  state.description = props.audienceList?.description ?? '';
  v$.value.$reset();
};

watch(() => props.audienceList, syncFromProps, { immediate: true });

const nameError = computed(() =>
  v$.value.name.$error ? t('CAMPAIGN.AUDIENCE.FORM.NAME.ERROR') : ''
);

const handleConfirm = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const payload = { name: state.name, description: state.description };
    const saved = isEditing.value
      ? await store.dispatch('audienceLists/update', {
          id: props.audienceList.id,
          ...payload,
        })
      : await store.dispatch('audienceLists/create', payload);

    useAlert(
      t(
        isEditing.value
          ? 'CAMPAIGN.AUDIENCE.API.UPDATE_SUCCESS'
          : 'CAMPAIGN.AUDIENCE.API.CREATE_SUCCESS'
      )
    );
    emit('saved', saved);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.AUDIENCE.API.ERROR_MESSAGE')
    );
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? t('CAMPAIGN.AUDIENCE.EDIT.TITLE')
        : t('CAMPAIGN.AUDIENCE.CREATE.TITLE')
    "
    :description="t('CAMPAIGN.AUDIENCE.CREATE.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.AUDIENCE.FORM.BUTTONS.SAVE')"
    :is-loading="isSaving"
    :disable-confirm-button="isSaving"
    @confirm="handleConfirm"
    @close="syncFromProps"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('CAMPAIGN.AUDIENCE.FORM.NAME.LABEL')"
        :placeholder="t('CAMPAIGN.AUDIENCE.FORM.NAME.PLACEHOLDER')"
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
      />
      <TextArea
        v-model="state.description"
        :label="t('CAMPAIGN.AUDIENCE.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('CAMPAIGN.AUDIENCE.FORM.DESCRIPTION.PLACEHOLDER')"
      />
    </div>
  </Dialog>
</template>
